import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  Download, Printer, Search,
  FileText, Calendar,
  CheckCircle, AlertCircle
} from 'lucide-react';

function SkeletonRow({ cols = 4 }) {
  return (
    <div className="flex gap-4 items-center py-3 px-6">
      {[...Array(cols)].map((_, i) => (
        <div key={i} className="skeleton h-4 flex-1" />
      ))}
    </div>
  );
}

function AccountDetailRow({ account, showNormalBalance }) {
  const normalBalanceClass = account.normal_balance === 'debit'
    ? 'text-primary-900 dark:text-primary-400'
    : 'text-danger-700 dark:text-danger-400';

  return (
    <div className="flex items-center justify-between py-2 px-12">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-400 w-16 font-mono">{account.code}</span>
        <span className="text-sm text-gray-600 dark:text-gray-400">-</span>
        <span className="text-sm text-gray-700 dark:text-gray-300">{account.name}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-mono ${normalBalanceClass}`}>{formatCurrency(account.balance)}</span>
        {showNormalBalance && (
          <span className="text-xs text-gray-400">({account.normal_balance === 'debit' ? 'DB' : 'KR'})</span>
        )}
      </div>
    </div>
  );
}

function BalanceRow({ label, amount, bold, highlight, balanced }) {
  return (
    <div className={`flex items-center justify-between py-2.5 px-6 ${bold ? 'font-semibold' : ''} ${highlight ? 'bg-primary-50 dark:bg-primary-900/20 border-y border-primary-200 dark:border-primary-700' : ''} ${balanced ? 'pl-6' : 'pl-12'}`}>
      <span className={`text-sm ${bold ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>{label}</span>
      <span className={`text-sm ${bold ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'} ${balanced ? '' : ''} ${balanceCheckClass(amount)}`}>{
        formatCurrency(amount)
      }</span>
    </div>
  );
}

function BalanceRowWithCheck({ assets, liabilities, equity }) {
  const isBalanced = assets === liabilities + equity;

  return (
    <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">VALIDASI NERACA</h4>
        {isBalanced ? (
          <div className="flex items-center gap-1 text-success-600 dark:text-success-400">
            <CheckCircle className="h-4 w-4" />
            <span className="text-xs font-medium">Terverifikasi</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-danger-600 dark:text-danger-400">
            <AlertCircle className="h-4 w-4" />
            <span className="text-xs font-medium">Tidak Seimbang</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Aset</p>
          <p className="text-base font-semibold text-primary-900 dark:text-primary-400">{formatCurrency(assets)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Kewajiban</p>
          <p className="text-base font-semibold text-danger-700 dark:text-danger-400">{formatCurrency(liabilities)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Ekuitas</p>
          <p className="text-base font-semibold text-success-600 dark:text-success-400">{formatCurrency(equity)}</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-center gap-2 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Aset:</span>
          <span className="font-mono {isBalanced ? 'text-success-600 dark:text-success-400 font-semibold' : 'text-danger-600 dark:text-danger-400 font-semibold'}">
            {formatCurrency(assets)} = {formatCurrency(liabilities + equity)}
          </span>
        </div>
      </div>
    </div>
  );
}

function balanceCheckClass() {
  return '';
}

export default function BalanceSheetPage() {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [period, setPeriod] = useState(currentMonth);
  const [comparePeriod, setComparePeriod] = useState('');
  const [showCompare, setShowCompare] = useState(false);
  const [data, setData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [period]);

  useEffect(() => {
    if (showCompare && comparePeriod) {
      fetchCompareData();
    }
  }, [comparePeriod, showCompare]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/balance-sheet', { params: { period } });
      setData(res.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompareData = async () => {
    try {
      const res = await api.get('/reports/balance-sheet', { params: { period: comparePeriod } });
      setCompareData(res.data.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const toggleCompare = () => {
    if (showCompare) {
      setShowCompare(false);
      setCompareData(null);
      setComparePeriod('');
    } else {
      setShowCompare(true);
    }
  };

  const formatPeriodLabel = (p) => {
    if (!p) return '';
    const [y, m] = p.split('-');
    const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Laporan Neraca</h1>
          <p className="page-subtitle">Laporan Posisi Keuangan (Balance Sheet)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => toast.success('Export PDF - Coming Soon')} className="btn btn-outline btn-sm">
            <Download className="h-4 w-4 mr-1.5" /> PDF
          </button>
          <button onClick={() => toast.success('Export Excel - Coming Soon')} className="btn btn-outline btn-sm">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </button>
          <button onClick={() => window.print()} className="btn btn-outline btn-sm">
            <Printer className="h-4 w-4 mr-1.5" /> Cetak
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="label">Periode</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="month"
                  className="input pl-9"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                />
              </div>
            </div>
            <div>
              <button onClick={toggleCompare} className={`btn ${showCompare ? 'btn-primary' : 'btn-outline'} btn-sm mt-6`}>
                {showCompare ? 'Bandingkan Aktif' : 'Bandingkan Periode'}
              </button>
            </div>
            {showCompare && (
              <div>
                <label className="label">Periode Pembanding</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="month"
                    className="input pl-9"
                    value={comparePeriod}
                    onChange={(e) => setComparePeriod(e.target.value)}
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button onClick={fetchData} className="btn btn-primary btn-sm">
                <Search className="h-4 w-4 mr-1.5" /> Muat Ulang
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="card">
          <div className="card-header">
            <div className="skeleton h-5 w-48" />
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {[...Array(20)].map((_, i) => <SkeletonRow key={i} />)}
          </div>
        </div>
      ) : !data ? (
        <div className="card">
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Data laporan neraca tidak tersedia untuk periode ini.</p>
          </div>
        </div>
      ) : (
        <div className={`grid ${showCompare && compareData ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-6`}>
          {/* Main Report */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                <FileText className="h-5 w-5 mr-2 inline" />
                Laporan Neraca
              </h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">{formatPeriodLabel(period)}</span>
            </div>

            {/* Assets Section */}
            <div className="border-b border-gray-100 dark:border-gray-700">
              <div className="bg-primary-50 dark:bg-primary-950/50 py-3 px-6">
                <h4 className="text-sm font-semibold text-primary-900 dark:text-primary-100 uppercase tracking-wide">ASET</h4>
              </div>
              {data.assets?.accounts?.map((acc) => (
                <AccountDetailRow key={acc.id} account={acc} showNormalBalance={true} />
              ))}
              <BalanceRow label="TOTAL ASET" amount={data.assets?.total} bold />
            </div>

            {/* Liabilities Section */}
            <div className="border-b border-gray-100 dark:border-gray-700">
              <div className="bg-danger-50 dark:bg-danger-900/30 py-3 px-6">
                <h4 className="text-sm font-semibold text-danger-900 dark:text-danger-100 uppercase tracking-wide">KEWAJIBAN</h4>
              </div>
              {data.liabilities?.accounts?.map((acc) => (
                <AccountDetailRow key={acc.id} account={acc} showNormalBalance={true} />
              ))}
              <BalanceRow label="TOTAL KEWAJIBAN" amount={data.liabilities?.total} bold />
            </div>

            {/* Equity Section */}
            <div className="border-b border-gray-100 dark:border-gray-700">
              <div className="bg-success-50 dark:bg-success-900/30 py-3 px-6">
                <h4 className="text-sm font-semibold text-success-900 dark:text-success-100 uppercase tracking-wide">EKUITAS</h4>
              </div>
              {data.equity?.accounts?.map((acc) => (
                <AccountDetailRow key={acc.id} account={acc} showNormalBalance={true} />
              ))}
              <BalanceRow label="TOTAL EKUITAS" amount={data.equity?.total} bold />
            </div>

            {/* Balance Check */}
            {data.balance_check && (
              <BalanceRowWithCheck
                label="NERACA CHECK"
                assets={data.assets?.total}
                liabilities={data.liabilities?.total}
                equity={data.equity?.total}
                period={period}
              />
            )}
          </div>

          {/* Compare Report */}
          {showCompare && compareData && (
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  <FileText className="h-5 w-5 mr-2 inline" />
                  Perbandingan
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">{formatPeriodLabel(comparePeriod)}</span>
              </div>

              <BalanceRow label="TOTAL ASET" amount={compareData.assets?.total} bold />
              <BalanceRow label="TOTAL KEWAJIBAN" amount={compareData.liabilities?.total} bold />
              <BalanceRow label="TOTAL EKUITAS" amount={compareData.equity?.total} bold />
              {compareData.balance_check && (
                <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-center gap-2 text-xs">
                    <span className="text-gray-600 dark:text-gray-400">Terverifikasi:</span>
                    <CheckCircle className="h-3 w-3 text-success-600 dark:text-success-400" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
